import React, { useContext, useEffect, useState } from 'react';

import { Button } from '@components/Button';
import { ErrorMessage } from '@components/Forms/ErrorMessage';
import { DeliveryInstructionContext } from '@components/utilities/Bookings/helpers';
import { ContactForm } from '@components/utilities/Contact/ContactForm';
import { InstructionContactDisplay } from '@components/utilities/Contact/InstructionContactDisplay';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { instructionStrings } from '@data/instruction';
import { usePatchDeliveryInstruction as useDashboardPatchDeliveryInstruction } from '@hooks/mutation/dashboard/usePatchDeliveryInstruction';
import { usePatchDeliveryInstruction } from '@hooks/mutation/usePatchDeliveryInstruction';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { areContactsEqual, contactFormInitialValues } from '@models/Contact';
import { DeliveryInstructionReceiverFormLoose } from '@models/DeliveryInstruction';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { useField } from 'formik';

const strings = createStrings({
  ja: {
    changeReceiverHint: `*${bookingInquiryStrings.receiver}の変更が必要な場合、運送費用の変更が伴います。ご了承ください。`,
    changeReceiver: `${bookingInquiryStrings.receiver}を変更する`,
    abortChangingReceiver: `${bookingInquiryStrings.receiver}の変更をやめる`,
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  contactContainer: {
    alignSelf: 'stretch',
    background: theme.palette.grey[50],
    padding: '20px 30px',
  },
  hint: {
    ...common.textGray700,
    ...common.smallerText,
    ...common.flexInline,
    ...common.justifyBetween,
  },
  hintWarning: {
    ...common.smallerText,
    color: theme.palette.warning.main,
  },
  changeReceiver: {
    ...common.link,
    ...common.baseButton,
    ...common.fontWeight600,
    float: 'right',
    '&$disabled': {
      ...common.textGray700,
      cursor: 'not-allowed',
      textDecoration: 'initial',
    },
  },
  disabled: {},
  checkboxLabel: {
    ...common.middleText,
    ...common.textGray800,
    marginLeft: 0,
  },
  checkboxRoot: {
    padding: 0,
    marginRight: 5,
  },
  labelRoot: {
    marginLeft: 0,
    cursor: 'initial',
  },
  contactForm: {
    '& label': {
      ...common.middleText,
      ...common.textGray700,
    },
  },
}));

export interface DeliveryInstructionReceiverInputProps {}

export type ChangeState = 'NOT_CHANGING' | 'CHANGING' | 'CHANGED';

export const DeliveryInstructionReceiverInput: React.FC<
  DeliveryInstructionReceiverInputProps
> = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [field, meta, helpers] = useField<DeliveryInstructionReceiverFormLoose>('receiver');

  const { value } = field;

  const { d2dNo, deliveryInstruction, setDeliveryInstruction, isEditing, type } = useContext(
    DeliveryInstructionContext,
  );

  const isReceiverChanged = deliveryInstruction.receiver
    ? !areContactsEqual(deliveryInstruction.old_receiver, deliveryInstruction.receiver)
    : false;

  const initialChangeState = isReceiverChanged ? 'CHANGED' : 'NOT_CHANGING';

  const [changeState, setChangeState] = useState<ChangeState>(initialChangeState);

  useEffect(() => {
    setChangeState(initialChangeState);
  }, [initialChangeState]);

  const patchDeliveryInstruction =
    type === 'client'
      ? usePatchDeliveryInstruction(d2dNo)
      : useDashboardPatchDeliveryInstruction(d2dNo);

  const renderChangeReceiver = () => (
    <>
      <div className={classes.hint}>
        <div style={{ maxWidth: 316 }}>{strings.changeReceiverHint}</div>

        <Button
          color="outlined"
          type="button"
          text={
            <>
              {changeState === 'NOT_CHANGING' && formStrings.change}
              {changeState === 'CHANGING' && formStrings.cancel}
              {changeState === 'CHANGED' && formStrings.edit}
            </>
          }
          size="small"
          disabled={value.tag === 'RECEIVER_CONFIRMED' || !isEditing}
          onClick={() => {
            if (value.tag === 'RECEIVER_CHANGE') {
              switch (changeState) {
                case 'NOT_CHANGING':
                  setChangeState('CHANGING');
                  helpers.setValue({
                    tag: 'RECEIVER_CHANGE',
                    receiver: deliveryInstruction.old_receiver,
                  });
                  break;
                case 'CHANGING':
                  helpers.setValue({
                    tag: 'RECEIVER_CHANGE',
                    receiver: contactFormInitialValues,
                  });
                  setChangeState('NOT_CHANGING');
                  break;
                case 'CHANGED':
                  setChangeState('CHANGING');
                  break;
              }
            }
          }}
        />
      </div>
      {changeState === 'CHANGING' && (
        <>
          <ContactForm
            name="receiver.receiver"
            className={classes.contactForm}
            disabled={!isEditing}
            zipcodeRequired
            contactType="receiver"
          />
          <div>
            <Button
              color="outlined"
              text={formStrings.update}
              size="small"
              disabled={!!meta.error || !isEditing}
              isLoading={patchDeliveryInstruction.isLoading}
              type="button"
              onClick={async () => {
                try {
                  const updated = await patchDeliveryInstruction.mutateAsync({
                    is_receiver_checked: value.tag === 'RECEIVER_CONFIRMED',
                    receiver:
                      value.tag === 'RECEIVER_CONFIRMED'
                        ? deliveryInstruction.old_receiver
                        : value.receiver,
                  });
                  setDeliveryInstruction(updated);
                  setChangeState('CHANGED');
                } catch (e) {
                  alert((e as any)?.message);
                }
              }}
            />
          </div>
        </>
      )}
      {changeState === 'CHANGED' && (
        <InstructionContactDisplay contact={deliveryInstruction.receiver!} />
      )}
    </>
  );

  return (
    <>
      <FormControlLabel
        control={
          <Checkbox
            checked={value.tag === 'RECEIVER_CONFIRMED'}
            color="primary"
            classes={{ root: classes.checkboxRoot }}
            disabled={changeState !== 'NOT_CHANGING' || !isEditing}
          />
        }
        label={instructionStrings.hasConfirmedReceiver}
        onClick={() => {
          if (changeState === 'NOT_CHANGING') {
            if (value.tag === 'RECEIVER_CONFIRMED') {
              helpers.setValue({
                tag: 'RECEIVER_CHANGE',
                receiver: contactFormInitialValues,
              });
            } else {
              helpers.setValue({
                tag: 'RECEIVER_CONFIRMED',
              });
            }
          }
        }}
        classes={{
          label: classes.checkboxLabel,
          root: classes.labelRoot,
        }}
      />

      <ErrorMessage name="receiver.tag" />

      <InstructionContactDisplay
        contact={deliveryInstruction.old_receiver}
        stale={changeState !== 'NOT_CHANGING'}
      />

      {renderChangeReceiver()}
    </>
  );
};
